import { TypeProduct } from '@/core/generated-types';
import { KIT_ANCHORS, mapTitleToAnchor } from '../utils';
import {
  ExtendedProduct,
  ExtendedProductFields,
} from '@/core/contentfulConfig';
import { MoneyVariant } from '@/lib/shopify/types';
import { formatMoney } from '@/lib/shopify/utils';
import { convertCentsToDollars } from '@/utils/currency';
import { pushEvent, captureProductFieldsEvent } from '@/utils/dataLayer';
import {
  PRODUCT_ADDED_COMPONENT_TYPES,
  PRODUCT_EVENT_TYPES,
} from '@/utils/rudderstack';
import { SelectedAddOns } from '@/components/common/Tariff/TariffPDPMessage';

enum TariffRateOptions {
  BABY = 'baby',
  TODDLER = 'toddler',
}

const TARIFF_PERCENTAGE_VALUES = {
  [TariffRateOptions.BABY]: 13,
  [TariffRateOptions.TODDLER]: 17,
};

export const getProductTariff = (product: TypeProduct) => {
  const price = product.fields.shopifyProduct?.variants[0].price?.amount || 0;

  const {
    BABBLER,
    ADVENTURER,
    REALIST,
    COMPANION,
    HELPER,
    ENTHUSIAST,
    RESEARCHER,
    FREE_SPIRIT,
    OBSERVER,
    STORYTELLER,
    PROBLEM_SOLVER,
    ANALYST,
    CONNECTOR,
    EXAMINER,
    PERSISTER,
    PLANNER,
  } = KIT_ANCHORS;

  const TODDLER_KIT_ANCHORS = [
    BABBLER,
    ADVENTURER,
    REALIST,
    COMPANION,
    HELPER,
    ENTHUSIAST,
    RESEARCHER,
    FREE_SPIRIT,
    OBSERVER,
    STORYTELLER,
    PROBLEM_SOLVER,
    ANALYST,
    CONNECTOR,
    EXAMINER,
    PERSISTER,
    PLANNER,
  ];

  const anchor = mapTitleToAnchor(
    product?.fields.shopifyProduct?.title || product.fields.title || '',
  );

  const isToddlerKit = anchor && TODDLER_KIT_ANCHORS.includes(anchor);

  const tariffRate =
    TARIFF_PERCENTAGE_VALUES[
      isToddlerKit ? TariffRateOptions.TODDLER : TariffRateOptions.BABY
    ];

  const rawSurcharge = price * (tariffRate / 100);

  const integerSurcharge = Math.round(rawSurcharge);
  // note: added a Number() wrapper here to ensure that either way we return a consistent type
  const twoDecimalSurcharge = Number(rawSurcharge.toFixed(2));

  return product.fields.isPlayKit ? integerSurcharge : twoDecimalSurcharge;
};

export function createSubscriptionProduct(
  product: ExtendedProduct,
): ExtendedProduct {
  const translations = product.fields.translations?.fields;

  const subscriptionProduct = {
    ...product,
    fields: {
      ...product.fields,
      strikethroughPrice: translations?.strikethroughPriceStandalone,
      title: translations?.subscribeAndSaveLabel || 'Subscribe & Save',
    },
  };

  if (subscriptionProduct.fields.cta && translations?.addToCartSubscribe) {
    subscriptionProduct.fields.cta = {
      ...subscriptionProduct.fields.cta,
      fields: {
        ...subscriptionProduct.fields.cta?.fields,
        linkText: translations.addToCartSubscribe,
      },
    };
  }

  return subscriptionProduct;
}

export function createStandaloneProduct(
  product: ExtendedProduct,
  locale: string,
): ExtendedProduct | undefined {
  const translationFields = { ...product.fields.translations?.fields };
  const baseProduct = { ...product };
  const { shopifyProduct } = baseProduct.fields as ExtendedProductFields;
  delete translationFields.badgeStandalone;

  if (
    !shopifyProduct ||
    !shopifyProduct.variants ||
    !shopifyProduct.variants.some((v) => !!v.priceCentsStandalone)
  ) {
    return undefined;
  }

  const standaloneVariants = shopifyProduct.variants.map((variant) => {
    const standalonePriceCents = variant.priceCentsStandalone ?? 0;

    const standalonePrice = {
      ...variant.price,
      amount: convertCentsToDollars(standalonePriceCents),
    } as MoneyVariant;

    return {
      ...variant,
      formattedPrice: formatMoney(standalonePrice, locale),
      price: standalonePrice,
    };
  });

  const standaloneProduct = {
    ...baseProduct,
    sys: {
      ...baseProduct.sys,
      id: `${baseProduct.sys.id}-standalone`,
    },
    fields: {
      ...baseProduct.fields,
      title: translationFields?.singlePlayKitLabel || 'Single Play Kit',
      optionDetails: undefined,
      translations: {
        ...product.fields.translations,
        fields: translationFields,
      },
      shopifyProduct: {
        ...shopifyProduct,
        variants: standaloneVariants,
      },
    } as ExtendedProductFields,
  };

  if (standaloneProduct.fields.cta && translationFields?.addToCartStandalone) {
    standaloneProduct.fields.cta = {
      ...standaloneProduct.fields.cta,
      fields: {
        ...standaloneProduct.fields.cta?.fields,
        linkText: translationFields.addToCartStandalone,
      },
    };
  }

  return standaloneProduct;
}

/**
 * Tracks add-on events for selected add-ons. Ensures all events are fired before continuing.
 * @param selectedAddOns Array of selected add-ons
 */
export async function trackAddOnEvents(selectedAddOns: SelectedAddOns[]) {
  if (!selectedAddOns) return;
  for (const selectedAddOn of selectedAddOns) {
    const shopifyProductAddOn = selectedAddOn?.fields?.shopifyProduct;
    if (shopifyProductAddOn) {
      await Promise.resolve(
        pushEvent('addToCart', {
          qtyVal: 1,
          shopifyProduct: shopifyProductAddOn,
          variant: selectedAddOn.variant,
        }),
      );
      let priceDisplay: string | number = '';

      priceDisplay = selectedAddOn.variant.priceCentsStandalone
        ? selectedAddOn.variant.priceCentsStandalone / 100
        : selectedAddOn.variant.formattedPrice || 0;

      await Promise.resolve(
        captureProductFieldsEvent(
          PRODUCT_EVENT_TYPES.PRODUCT_ADDED,
          {
            shopifyProduct: shopifyProductAddOn,
            variant: selectedAddOn.variant,
            product: selectedAddOn.fields,
            quantity: 1,
            url: window.location.href,
          },
          PRODUCT_ADDED_COMPONENT_TYPES.UPSELL_PDP,
          undefined,
          priceDisplay,
        ),
      );
    }
  }
}
